import api from './api';

export type ReadinessChecks = Record<string, string>;

export interface ReadinessResponse {
  status: 'ready' | 'degraded' | string;
  checks: ReadinessChecks;
}

/** GET /api/health/ready — DB·게이트웨이 HLS·YOLO 등 */
export async function fetchReadiness(): Promise<ReadinessResponse> {
  return api.get<ReadinessResponse>('/api/health/ready');
}

export interface EventLatencySnapshot {
  count: number;
  avg_ms: number;
  p95_ms: number;
}

export async function fetchEventLatencyMetrics(): Promise<EventLatencySnapshot> {
  const raw = await api.get<EventLatencySnapshot>('/api/metrics/event-latency');
  return {
    count: Number(raw.count ?? 0),
    avg_ms: Number(raw.avg_ms ?? 0),
    p95_ms: Number(raw.p95_ms ?? 0),
  };
}

const READINESS_SCORE_KEYS = new Set([
  'database',
  'gateway_hls',
  'yolo',
  'gpu_allocation',
]);

function isReadinessCheckPass(value: string): boolean {
  const v = String(value || '').toLowerCase();
  return v === 'ok' || v === 'true' || v === 'unloaded';
}

export type SystemHealthSummary = {
  readinessStatus: string;
  readinessScore: number;
  readinessPass: number;
  readinessTotal: number;
  checkLabels: { key: string; label: string; value: string; ok: boolean }[];
  eventLatencyCount: number;
  eventLatencyP95Ms: number;
  cctvCollectionRate: number;
  centralAiEnabled: boolean;
  centralAiJobCount: number;
  activeIncidents: number;
  resolvedIncidents: number;
};

const CHECK_KO: Record<string, string> = {
  database: 'DB',
  gateway_hls: '게이트웨이 HLS',
  yolo: 'YOLO',
  openpose_enabled: 'OpenPose',
  gpu_allocation: 'GPU 할당',
  gpu_device_count: 'GPU 장치 수',
};

/** 대시보드 「시스템 상태」 — readiness·CCTV·AI·이벤트 집계 */
export async function fetchSystemHealthSummary(opts?: {
  cctvCount?: number;
  onlineCctvCount?: number;
}): Promise<SystemHealthSummary> {
  const cctvCount = Math.max(0, opts?.cctvCount ?? 0);
  const onlineCctvCount = Math.max(0, opts?.onlineCctvCount ?? 0);
  const cctvCollectionRate =
    cctvCount > 0
      ? Math.round((onlineCctvCount / cctvCount) * 1000) / 10
      : 0;

  let readinessStatus = 'offline';
  let readinessScore = 0;
  let readinessPass = 0;
  let readinessTotal = 0;
  let checkLabels: SystemHealthSummary['checkLabels'] = [];
  let eventLatencyCount = 0;
  let eventLatencyP95Ms = 0;
  let centralAiEnabled = false;
  let centralAiJobCount = 0;
  let activeIncidents = 0;
  let resolvedIncidents = 0;

  try {
    const [ready, latency, aiSt, eventsRes] = await Promise.all([
      fetchReadiness(),
      fetchEventLatencyMetrics().catch(() => ({
        count: 0,
        avg_ms: 0,
        p95_ms: 0,
      })),
      api
        .get<{
          server_ai_enabled?: boolean;
          inference_job_count?: number;
        }>('/api/central-ai-monitor/status')
        .catch(() => null),
      api
        .get<{ items?: { status?: string }[]; total?: number }>(
          '/api/events/',
          { limit: 300 },
          { useCache: false }
        )
        .catch(() => null),
    ]);

    readinessStatus = ready.status || 'unknown';
    const checks = ready.checks || {};
    const scored = Object.entries(checks).filter(([k]) =>
      READINESS_SCORE_KEYS.has(k)
    );
    readinessTotal = scored.length;
    readinessPass = scored.filter(([, v]) => isReadinessCheckPass(v)).length;
    readinessScore =
      readinessTotal > 0
        ? Math.round((readinessPass / readinessTotal) * 1000) / 10
        : 0;

    checkLabels = Object.entries(checks).map(([key, value]) => ({
      key,
      label: CHECK_KO[key] || key,
      value: String(value),
      ok: isReadinessCheckPass(String(value)),
    }));

    eventLatencyCount = latency.count;
    eventLatencyP95Ms = latency.p95_ms;

    if (aiSt) {
      centralAiEnabled = Boolean(aiSt.server_ai_enabled);
      centralAiJobCount = Number(aiSt.inference_job_count ?? 0);
    }

    const items = Array.isArray(eventsRes?.items) ? eventsRes!.items! : [];
    const resolvedStatuses = new Set([
      'resolved',
      'auto_resolved',
      'acknowledged',
      'closed',
      'handled',
    ]);
    for (const ev of items) {
      const st = String(ev.status || '').toLowerCase();
      if (resolvedStatuses.has(st)) {
        resolvedIncidents += 1;
      } else {
        activeIncidents += 1;
      }
    }
  } catch {
    readinessStatus = 'offline';
  }

  return {
    readinessStatus,
    readinessScore,
    readinessPass,
    readinessTotal,
    checkLabels,
    eventLatencyCount,
    eventLatencyP95Ms,
    cctvCollectionRate,
    centralAiEnabled,
    centralAiJobCount,
    activeIncidents,
    resolvedIncidents,
  };
}
